import React, { useMemo, useSyncExternalStore } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { buildTrackGraph } from '../../utils/simulationGraph';
import { getExportableTrackEdges } from '../../utils/simulationTrackNetworkView';
import { ALL_RAIL_TYPES, getAABB } from '../../utils/topology';
import {
  boundsFromPoints,
  collectOhtTravelPathLines,
  collectOhtTraveledSegmentIds,
  normalizeTraveledSegmentIds,
  segmentIsTraveled,
  type FloorBounds,
} from '../../utils/ohtFleetMetrics';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { liveMotionStore } from '../../store/liveMotionStore';
import type { AmhsFinalSimulationLeg, BrowserKineticVehicleLeg } from '../../types/simulationPlayback';
import type { LayoutElement } from '../../types';

type Props = {
  ohtIds: string[];
  simTime: number;
  pathColor?: string;
  height?: number;
};

const PAD = 14;
const VIEW_WIDTH = 320;

const LAYOUT_MARKER_TYPES = new Set<LayoutElement['type']>(['stocker', 'stb', 'ohb', 'lifter']);

function buildViewTransform(bounds: FloorBounds, width: number, height: number) {
  const spanX = Math.max(bounds.maxX - bounds.minX, 1);
  const spanY = Math.max(bounds.maxY - bounds.minY, 1);
  const scale = Math.min((width - PAD * 2) / spanX, (height - PAD * 2) / spanY);
  const drawW = spanX * scale;
  const drawH = spanY * scale;
  const offsetX = PAD + (width - PAD * 2 - drawW) / 2;
  const offsetY = PAD + (height - PAD * 2 - drawH) / 2;

  const mapPoint = (x: number, y: number) => ({
    x: offsetX + (x - bounds.minX) * scale,
    y: offsetY + (y - bounds.minY) * scale,
  });

  return { mapPoint, scale };
}

function resolveLegsByVehicle(
  running: boolean,
  legacy: Record<string, BrowserKineticVehicleLeg[]>
): Record<string, BrowserKineticVehicleLeg[]> {
  if (!running) return legacy;
  const playback = liveMotionStore.getPlaybackVehicles();
  const fromPlayback: Record<string, BrowserKineticVehicleLeg[]> = {};
  for (const [ohtId, vehicle] of Object.entries(playback)) {
    fromPlayback[ohtId] = vehicle.legs;
  }
  return Object.keys(fromPlayback).length > 0 ? fromPlayback : legacy;
}

/**
 * Mini layout map — track network, equipment markers, and this OHT's traveled area.
 */
const OhtTravelHistoryMap: React.FC<Props> = ({
  ohtIds,
  simTime,
  pathColor = '#0284c7',
  height = 200,
}) => {
  const elements = useAppSelector((state) => state.layout.elements);
  const running = useAppSelector((state) => state.simulation.running);
  const legacyApiLegs = useAppSelector(
    (state) => state.simulation.browserKinetic?.apiLegs ?? []
  );
  const legacyLegsByVehicle = useAppSelector(
    (state) => state.simulation.browserKinetic?.vehicleLegs.legsByVehicle ?? {}
  );

  useSyncExternalStore(
    (onStoreChange) => (running ? liveMotionStore.subscribe(onStoreChange) : () => {}),
    () => (running ? liveMotionStore.getRevision() : 0),
    () => 0
  );

  const apiLegs = running ? liveMotionStore.getApiLegs() : legacyApiLegs;
  const legsByVehicle = resolveLegsByVehicle(running, legacyLegsByVehicle);

  const mapModel = useMemo(() => {
    const layoutList = Object.values(elements).filter((el) => el.metadata.visible !== false);
    const railList = layoutList.filter((el) => ALL_RAIL_TYPES.has(el.type));
    const graph = buildTrackGraph(railList.length > 0 ? railList : layoutList);
    const exportable = getExportableTrackEdges(graph, railList.length > 0 ? railList : layoutList);

    const railSegments = exportable
      .map(({ edge, segmentId }) => {
        const pts = edge.points;
        if (!pts || pts.length < 2) return null;
        return { segmentId, points: pts.map((p) => ({ x: p.x, y: p.y })) };
      })
      .filter(Boolean) as Array<{ segmentId: string; points: Array<{ x: number; y: number }> }>;

    const traveledSegmentIds = normalizeTraveledSegmentIds(
      collectOhtTraveledSegmentIds(ohtIds, apiLegs as AmhsFinalSimulationLeg[], simTime)
    );

    const travelLines = collectOhtTravelPathLines(ohtIds, legsByVehicle, simTime);

    const layoutMarkers = layoutList
      .filter((el) => LAYOUT_MARKER_TYPES.has(el.type))
      .map((el) => {
        const box = getAABB(el);
        return {
          id: el.id,
          minX: box.minX,
          minY: box.minY,
          maxX: box.maxX,
          maxY: box.maxY,
          type: el.type,
        };
      });

    let vehiclePoint: { x: number; y: number } | null = null;
    for (const ohtId of ohtIds) {
      const runtime = liveOhtRuntimeStore.getOht(ohtId);
      if (runtime && Number.isFinite(runtime.x) && Number.isFinite(runtime.y)) {
        vehiclePoint = { x: runtime.x, y: runtime.y };
        break;
      }
    }
    if (!vehiclePoint) {
      for (const ohtId of ohtIds) {
        const el = elements[ohtId];
        if (el?.type === 'oht-vehicle') {
          vehiclePoint = { x: el.position.x, y: el.position.y };
          break;
        }
      }
    }

    const fitPoints: Array<{ x: number; y: number }> = [];
    for (const segment of railSegments) fitPoints.push(...segment.points);
    for (const line of travelLines) {
      fitPoints.push({ x: line.x1, y: line.y1 }, { x: line.x2, y: line.y2 });
    }
    for (const marker of layoutMarkers) {
      fitPoints.push({ x: marker.minX, y: marker.minY }, { x: marker.maxX, y: marker.maxY });
    }
    if (vehiclePoint) fitPoints.push(vehiclePoint);

    const fitBounds = boundsFromPoints(fitPoints);

    return {
      railSegments,
      traveledSegmentIds,
      travelLines,
      layoutMarkers,
      vehiclePoint,
      fitBounds,
    };
  }, [apiLegs, elements, legsByVehicle, ohtIds, simTime]);

  const { mapPoint } = useMemo(() => {
    const bounds = mapModel.fitBounds ?? { minX: 0, minY: 0, maxX: 400, maxY: 300 };
    return buildViewTransform(bounds, VIEW_WIDTH, height);
  }, [height, mapModel.fitBounds]);

  if (mapModel.railSegments.length === 0 && mapModel.travelLines.length === 0) {
    return (
      <p className="py-6 text-center text-[10px] text-slate-400">
        No track geometry in this layout.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className="overflow-hidden rounded-lg border border-slate-200 bg-white"
        style={{ height }}
        aria-label="OHT traveling area on layout"
      >
        <svg
          viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-hidden
        >
          {mapModel.layoutMarkers.map((marker) => {
            const tl = mapPoint(marker.minX, marker.minY);
            const br = mapPoint(marker.maxX, marker.maxY);
            const w = Math.max(br.x - tl.x, 2);
            const h = Math.max(br.y - tl.y, 2);
            return (
              <rect
                key={marker.id}
                x={tl.x}
                y={tl.y}
                width={w}
                height={h}
                rx={1.5}
                fill={marker.type === 'stocker' ? '#e2e8f0' : '#f1f5f9'}
                stroke="#94a3b8"
                strokeWidth={0.75}
                opacity={0.9}
              />
            );
          })}

          {mapModel.railSegments.map(({ segmentId, points }) => {
            const traveled = segmentIsTraveled(segmentId, mapModel.traveledSegmentIds);
            const mapped = points.map((p) => mapPoint(p.x, p.y));
            const d = mapped
              .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
              .join(' ');
            return (
              <path
                key={segmentId}
                d={d}
                fill="none"
                stroke={traveled ? pathColor : '#cbd5e1'}
                strokeWidth={traveled ? 3 : 1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={traveled ? 1 : 0.7}
              />
            );
          })}

          {mapModel.travelLines.map((line, index) => {
            const a = mapPoint(line.x1, line.y1);
            const b = mapPoint(line.x2, line.y2);
            return (
              <line
                key={`travel-${index}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="#38bdf8"
                strokeWidth={line.partial ? 2.5 : 3.5}
                strokeLinecap="round"
                strokeDasharray={line.partial ? '5 4' : undefined}
                opacity={0.95}
              />
            );
          })}

          {mapModel.vehiclePoint ? (
            <circle
              cx={mapPoint(mapModel.vehiclePoint.x, mapModel.vehiclePoint.y).x}
              cy={mapPoint(mapModel.vehiclePoint.x, mapModel.vehiclePoint.y).y}
              r={5}
              fill="#f97316"
              stroke="#ffffff"
              strokeWidth={1.75}
            />
          ) : null}
        </svg>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[9px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-slate-300" />
          Track
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-sky-600" />
          Traveled rail
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-sky-300" />
          Leg path
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
          Current
        </span>
      </div>
    </div>
  );
};

export default OhtTravelHistoryMap;
